import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { FacebookCampaign } from '@feathr/blackbox';
import { FacebookCampaignObjectiveMap, FacebookObjectiveType } from '@feathr/blackbox';
import { Alert, UniversalSelect } from '@feathr/components';
import { FACEBOOK_OBJECTIVES_REFACTOR_DATE } from '@feathr/extender/global';

import type { ICampaignObjective } from './FacebookCampaignObjective.formatters';
import { formatCampaignObjectiveOptionLabel } from './FacebookCampaignObjective.formatters';

interface IProps {
  campaign: FacebookCampaign;
}

function FacebookCampaignObjective({ campaign }: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const dateFirstPublished = campaign.get('date_first_published');
  const options: ICampaignObjective[] =
    !dateFirstPublished || dateFirstPublished > FACEBOOK_OBJECTIVES_REFACTOR_DATE
      ? [
          {
            id: FacebookObjectiveType.Reach,
            name: FacebookCampaignObjectiveMap.get(FacebookObjectiveType.Reach),
            description: t('Show ads to the maximum amount of people.'),
          },
          {
            id: FacebookObjectiveType.Traffic,
            name: FacebookCampaignObjectiveMap.get(FacebookObjectiveType.Traffic),
            description: t('Optimize this campaign for driving website visits.'),
          },
        ]
      : [
          {
            id: FacebookObjectiveType.Reach_Legacy,
            name: FacebookCampaignObjectiveMap.get(FacebookObjectiveType.Reach_Legacy),
            description: t('Show ads to the maximum amount of people.'),
          },
          {
            id: FacebookObjectiveType.Traffic_Legacy,
            name: FacebookCampaignObjectiveMap.get(FacebookObjectiveType.Traffic_Legacy),
            description: t('Optimize this campaign for driving website visits.'),
          },
        ];

  const selectedObjective = options.find((option) => option.id === campaign.get('objective_type'));

  function handleOnSelectSingle(option: ICampaignObjective): void {
    campaign.set({ objective_type: option.id });
  }

  return (
    <>
      <UniversalSelect<ICampaignObjective>
        disabled={!!campaign.get('date_first_published')}
        formatOptionLabel={formatCampaignObjectiveOptionLabel}
        helpText={t('What you want people to do when they see your ads')}
        label={t('Campaign objective')}
        name={'campaign_objective'}
        onSelectSingle={handleOnSelectSingle}
        options={options}
        placeholder={t('Choose a campaign objective')}
        required={true}
        value={selectedObjective}
      />
      <Alert title={t('Campaign objective cannot be changed after publishing')} type={'warning'} />
    </>
  );
}

export default observer(FacebookCampaignObjective);
